import { useEffect } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  sendWayaOfficialOtp,
  verifyWayaOfficialOtp,
} from "@/services/waya-official";
import { markWayaOfficialUnlocked } from "@/utils/auth-session";
import { showToast } from "@/redux/actions/toast-actions";
import OtpModal from "@/components/custom/otp-modal";

// Renders instead of the real page until verify-otp succeeds. Fires on
// mount regardless of how the Owner arrived (menu click or a direct URL
// hit) — a fresh code is required every time this session hasn't unlocked
// the page yet.
const WayaOfficialOtpGate = ({ onUnlocked }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sendOtpMutation = useMutation({
    mutationFn: sendWayaOfficialOtp,
    onError: (err) => {
      dispatch(
        showToast(err?.message ?? "Could not send OTP. Please retry.", "error"),
      );
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: verifyWayaOfficialOtp,
    onSuccess: () => {
      markWayaOfficialUnlocked();
      onUnlocked();
    },
  });

  useEffect(() => {
    sendOtpMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOtpSubmit = ({ otp }) => {
    verifyOtpMutation.mutate(otp);
  };

  const handleClose = () => {
    if (verifyOtpMutation.isPending) return;
    // Backing out of the gate is equivalent to not having access this
    // session — send them somewhere safe, don't leave a half-open page.
    navigate("/dashboard", { replace: true });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
      }}
    >
      {sendOtpMutation.isPending && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Sending verification code…
          </Typography>
        </Box>
      )}

      <OtpModal
        open={!sendOtpMutation.isPending}
        onClose={handleClose}
        onSubmit={handleOtpSubmit}
        isLoading={verifyOtpMutation.isPending}
        errorMsg={verifyOtpMutation.error?.message}
      />
    </Box>
  );
};

export default WayaOfficialOtpGate;